import React, {useState} from 'react';
import {Prompt} from 'react-router';

import {Content} from 'components';
import {Icon, Preloader, Toast} from 'components/ultra';
import Sidebar from 'containers/sidebar';
import {ReferenceType} from 'gql/types';
import {useDoorkeeper} from 'utilities/auth';
import {useReactRouter} from 'utilities/react-router';

import {useEditorQuery} from './graphql/Editor';
import {useSavePosterMutation} from './graphql/SavePoster';
import {useSavePreviewMutation} from './graphql/SavePreview';
import {useEditorAddCartItemMutation} from './graphql/EditorAddCartItem';
import {ButtonRound, Poster, SidebarControls} from './components';
import {Buttons} from './styles';

export function Editor() {
  useDoorkeeper();
  const {history, match} = useReactRouter<{id: string}>();
  const hasUrlNew = match.params.id === 'new';

  const {
    data: editor,
    error: editorError,
    loading: editorLoading,
  } = useEditorQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      posterId: match.params.id,
      hasPosterId: !hasUrlNew,
    },
  });
  const [addCartItem] = useEditorAddCartItemMutation();
  const [savePreview] = useSavePreviewMutation();
  const [savePoster] = useSavePosterMutation();

  const [feedbackToast, setFeedbackToast] = useState<{
    error?: boolean;
    content?: string;
  }>();

  const defaultEditorState = {
    loading: true,
    isModified: false,
    previewActive: hasUrlNew,
  };
  const [editorState, setEditorState] = useState(defaultEditorState);

  if (editorError) {
    return (
      <>
        <Sidebar />
        <Content fullHeight>{editorError.message}</Content>
      </>
    );
  }

  if (editorLoading) {
    return (
      <>
        <Sidebar />
        <Content fullHeight>
          <Preloader />
        </Content>
      </>
    );
  }

  function resetFeedbackToast() {
    setFeedbackToast(undefined);
  }

  function resetMemento() {
    history.push({pathname: '/poster/new'});
  }

  function addItemToCart() {
    addCartItem({
      variables: {
        productId: '1', // ID for digital poster
        reference: {
          id: match.params.id,
          type: ReferenceType.Poster,
        },
      },
    })
      .then(({data, error}) => {
        if (!error && data) {
          setFeedbackToast({
            content: 'Your Poster was added to your cart!',
          });
        }
      })
      .catch(() => {
        setFeedbackToast({
          content: 'Your item could not be added to your cart.',
        });
      });
  }

  function saveItem() {
    savePoster({
      variables: {
        posterId: hasUrlNew ? null : match.params.id,
        poster: mementoSettings,
      },
    }).then(({data, error}) => {
      if (!error && data) {
        setEditorState({
          ...editorState,
          isModified: false,
          previewActive: false,
        });

        setFeedbackToast({content: 'Your Poster was saved successfully!'});

        history.push({
          pathname: `/poster/${data.savePoster.poster.id}`,
        });
      }
    });
  }

  const showPreview = editorState.isModified || hasUrlNew;
  const memento = showPreview ? editor.me.preview : editor.me.poster;
  const mementoSettings = {
    activitiesStartDate: memento.activitiesStartDate,
    activitiesEndDate: memento.activitiesEndDate,
    activityTypes: memento.activityTypes,
    baseColor: memento.baseColor,
    highlightColor: memento.highlightColor,
    name: memento.name,
    variant: memento.variant,
  };
  const mementoPresets = {
    variants: editor.editor.posterVariants,
    activityTypes: memento.options.activityTypes,
    colors: memento.options.colors,
  };

  function handleChangeSidebarSettings(settings: any) {
    setEditorState({
      isModified: true,
      loading: true,
      previewActive: true,
    });
    savePreview({variables: {poster: settings}});
  }

  return (
    <>
      <Sidebar selected>
        <SidebarControls
          loading={editorLoading}
          presets={mementoPresets}
          settings={mementoSettings}
          onChange={handleChangeSidebarSettings}
        />
      </Sidebar>
      <Content fullHeight>
        {feedbackToast && (
          <Toast
            message={feedbackToast.content}
            onDismiss={resetFeedbackToast}
          />
        )}
        <Prompt
          when={editorState.isModified}
          message="Are you sure you want to leave? Your changes to the current poster will be lost."
        />
        <Buttons>
          <ButtonRound onClick={resetMemento} disabled={hasUrlNew}>
            <Icon icon="add" />
            <span>New poster</span>
          </ButtonRound>
          <ButtonRound onClick={saveItem} disabled={!editorState.isModified}>
            <Icon icon="save" />
            <span>Save poster</span>
          </ButtonRound>
          <ButtonRound
            onClick={addItemToCart}
            disabled={editorState.previewActive || hasUrlNew}
            uppercase
          >
            <Icon icon="cartAddFilled" transform={{x: -1, y: 1}} />
            <span>Add to cart</span>
          </ButtonRound>
        </Buttons>

        <Poster
          src={memento.image.url}
          loading={editorState.loading}
          onFinishLoading={() =>
            setEditorState({...editorState, loading: false})
          }
        />
      </Content>
    </>
  );
}
